import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    ActivationEnd,
    Data,
    Event as RouterEvent,
    NavigationEnd,
    Router,
} from '@angular/router';
import { AppFrontSettingsProp } from '@core/models/client.settings.model';
import { AppSettingsService } from '@core/services/app-settings.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';


const MAIN_CONTENT_ID: string = 'main-content';

@Injectable({
    providedIn: 'root',
})
export class RouteTitleService {
    private readonly title: Title = inject(Title);
    private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
    private readonly router: Router = inject(Router);
    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly appSettingsService: AppSettingsService = inject(AppSettingsService);
    private readonly liveAnnouncer: LiveAnnouncer = inject(LiveAnnouncer);
    private readonly document: Document = inject(DOCUMENT);

    constructor() {
        this.trySetTitleFromRouteData();
        this.manageFocusOnNavigation();
    }

    public setTitle(key: string, skipTranslation: boolean = false): void {
        if (skipTranslation) {
            this.title.setTitle(key);
        } else {
            this.translateService
                .get(key)
                .pipe(take(1))
                .subscribe((translatedValue: string) => {
                    this.title.setTitle(translatedValue);
                });
        }
    }

    private trySetTitleFromRouteData(): void {
        combineLatest([
            this.translateService.onLangChange.pipe(startWith(null)),
            this.router.events.pipe(
                //TODO: every route called both
                filter((event: RouterEvent) => event instanceof ActivationEnd),
                map(() => this.activatedRoute.snapshot),
                map((snapshot: ActivatedRouteSnapshot) => {
                    let route: ActivatedRouteSnapshot = snapshot;
                    while (route.firstChild) {
                        route = route.firstChild;
                    }

                    if (!route?.data?.title) {
                        this.title.setTitle(this.appSettingsService.getPropDesign(AppFrontSettingsProp.Meta)?.title);
                    }

                    return route.data;
                }),
                filter((data: Data) => data.title),
            ),
        ]).subscribe(([, data]: [LangChangeEvent, Data]) => {
            this.setTitle(data.title);
        });
    }

    private manageFocusOnNavigation(): void {
        this.router.events.pipe(
            filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
        ).subscribe(() => {
            // Deferred so the router has finished rendering the new route's view before focus moves.
            setTimeout(() => {
                const mainContent: HTMLElement = this.document.querySelector(`#${MAIN_CONTENT_ID}`);

                mainContent?.focus();
                this.liveAnnouncer.announce(this.title.getTitle());
            });
        });
    }
}
